import type { FormEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { ChildSelector } from '@/components/builder/ChildSelector';
import { MediaOptionsGroup } from '@/components/builder/MediaOptionsGroup';
import { RandomToggle } from '@/components/builder/RandomToggle';
import {
  StorydurationSelector,
  type StoryDuration,
} from '@/components/builder/StorydurationSelector';
import { SubmitButton } from '@/components/builder/SubmitButton';
import { ThemeInput } from '@/components/builder/ThemeInput';
import { VoiceSelector, type Voice } from '@/components/builder/VoiceSelector';
import { BuilderGate } from '@/components/builder/BuilderGate';
import { SeriesModeToggle, type BuilderMode } from '@/components/builder/series/SeriesModeToggle';
import { SeriesBuilderForm } from '@/components/builder/series/SeriesBuilderForm';
import { useSeriesEnabled } from '@/hooks/useSeriesEnabled';
import { useBuilderUrlPrefill } from './use-builder-url-prefill';
import { clearEntitlementCache } from '@/hooks/useEntitlement';
import { clearSubscriptionStateCache } from '@/hooks/useSubscriptionState';
import { useStoryWorlds } from '@/hooks/useStoryWorlds';
import { apiFetch, ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

// Shape of POST /api/stories/jobs body. Source: src/story-jobs/dto/create-story-job.dto.ts.
interface CreateStoryJobRequest {
  worldId: string;
  childId: string;
  inputType: 'manual' | 'random';
  customTheme?: string;
  storyDuration: 'short' | 'standard' | 'long';
  narratorGender: 'male' | 'female';
  wantsAudio: boolean;
  wantsPdf: boolean;
}

interface CreateStoryJobResponse {
  jobId: string;
  status: string;
  totalSteps: number;
}

export function KPBuilderRoute() {
  const S = STRINGS.builder.shared;
  const SKP = STRINGS.builder.kp;
  const navigate = useNavigate();
  const { getWorldIdBySlug } = useStoryWorlds();

  // Batch 10 Sub-phase E — `?theme=` URL pre-fill from IdeaTile
  // (Inspiration-Tab) navigation. Hook captures-on-mount + clears
  // the URL via replace so back-button doesn't re-fill on refresh.
  const { initialValue: initialTheme } = useBuilderUrlPrefill();
  const { enabled: seriesEnabled } = useSeriesEnabled();
  const [mode, setMode] = useState<BuilderMode>('single');
  const [theme, setTheme] = useState(initialTheme ?? '');
  const [isRandom, setIsRandom] = useState(false);
  const [childId, setChildId] = useState<string | null>(null);
  const [storyduration, setStoryduration] = useState<StoryDuration>('standard');
  const [pdf, setPdf] = useState(false);
  const [audio, setAudio] = useState(false);
  const [voice, setVoice] = useState<Voice>('male');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Hoisted from SeriesBuilderForm via onPlanningChange. Used to hide the
  // SeriesModeToggle while the plan is being generated — the toggle is
  // inoperable during that wait.
  const [seriesPlanning, setSeriesPlanning] = useState(false);

  const submitDisabled = (!theme.trim() && !isRandom) || !childId;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled || isSubmitting) return;

    const worldId = getWorldIdBySlug('kp');
    if (!worldId || !childId) {
      console.error('[KPBuilder] missing worldId or childId', { worldId, childId });
      return;
    }

    const payload: CreateStoryJobRequest = {
      worldId,
      childId,
      inputType: isRandom ? 'random' : 'manual',
      storyDuration: storyduration,
      narratorGender: voice,
      wantsAudio: audio,
      wantsPdf: pdf,
    };
    if (!isRandom && theme.trim()) {
      payload.customTheme = theme.trim();
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const response = await apiFetch<CreateStoryJobResponse>('/api/stories/jobs', {
        method: 'POST',
        body: payload,
      });
      // B1 (Bug 2): clear-only (no eager refetch — deduction commits at pipeline
      // step 6, after this POST returns). B2/B3 refresh after it settles.
      clearEntitlementCache();
      clearSubscriptionStateCache();
      navigate(`/output/${response.jobId}`, {
        state: { world: 'kp', wantsAudio: audio, wantsPdf: pdf },
      });
      // Intentionally don't reset isSubmitting — we're navigating away.
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.envelope?.user_message ?? STRINGS.auth.errors.genericBackend);
      } else if (err instanceof NetworkError) {
        setSubmitError(STRINGS.auth.errors.networkError);
      } else {
        setSubmitError(STRINGS.auth.errors.genericBackend);
      }
      setIsSubmitting(false);
    }
  };

  const themePlaceholder = isRandom ? S.themePlaceholderRandom : S.themePlaceholderKP;

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/worlds/kp/stars-overlay.svg)' }}
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto max-w-[560px] px-6 pt-6 pb-12">
        <div className="mb-6 flex justify-center">
          <img
            src="/worlds/kp/kp-team.svg"
            alt=""
            aria-hidden="true"
            className="block h-[140px] w-auto md:h-[200px] desktop:h-[260px]"
          />
        </div>
        <h1 className="text-h3 mb-20 font-semibold text-dream-white">{SKP.pageTitle}</h1>

        <BuilderGate>
          {seriesEnabled && !seriesPlanning && (
            <div className="mb-5">
              <SeriesModeToggle
                value={mode}
                onChange={setMode}
                labelClassName="text-dream-white"
                dark
              />
            </div>
          )}
          {mode === 'series' ? (
            <SeriesBuilderForm worldSlug="kp" onPlanningChange={setSeriesPlanning} />
          ) : (
            <form onSubmit={handleSubmit}>
              <fieldset disabled={isSubmitting} className="flex flex-col gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="kp-theme" className="text-sm text-dream-white">
                    {S.themaLabel}
                  </Label>
                  <ThemeInput
                    id="kp-theme"
                    value={theme}
                    onChange={setTheme}
                    placeholder={themePlaceholder}
                    disabled={isRandom}
                    maxLength={300}
                  />
                </div>

                <RandomToggle
                  checked={isRandom}
                  onCheckedChange={setIsRandom}
                  labelClassName="text-dream-white"
                  trackClassName="border border-dream-white/40 bg-dream-white/20 data-[state=checked]:bg-dream-white/50"
                />

                <ChildSelector
                  id="kp-child"
                  label={S.childSelectorLabel}
                  labelClassName="text-dream-white"
                  value={childId}
                  onChange={setChildId}
                />

                <div className="space-y-1.5">
                  <Label htmlFor="kp-story-duration" className="text-sm text-dream-white">
                    {S.storydurationSelectorLabel}
                  </Label>
                  <StorydurationSelector
                    id="kp-story-duration"
                    value={storyduration}
                    onChange={setStoryduration}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-dream-white">{S.optionsLabel}</Label>
                  <MediaOptionsGroup
                    pdfActive={pdf}
                    audioActive={audio}
                    onPdfChange={setPdf}
                    onAudioChange={setAudio}
                  />
                </div>

                {audio && (
                  <VoiceSelector
                    value={voice}
                    onChange={setVoice}
                    containerClassName="bg-transparent"
                    headingClassName="text-dream-white"
                  />
                )}

                {submitError && (
                  <div
                    role="alert"
                    className="rounded-md border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-3 text-sm text-[color:var(--color-error)]"
                  >
                    {submitError}
                  </div>
                )}

                <SubmitButton disabled={submitDisabled} loading={isSubmitting} type="submit" />
              </fieldset>
            </form>
          )}
        </BuilderGate>
      </div>
    </div>
  );
}
